import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import Header from '@/components/layout/Header';
import { showSuccess, showError } from '@/lib/swal';
import { ArrowLeft, Gift, Loader2 } from 'lucide-react';

type UnitResultRow = {
  unitId: string;
  status: string;
  balanceAfter?: number;
  reason?: string;
  error?: string;
};

interface BulkRunRow {
  id: string;
  status?: string;
  creditsPerUnit?: number;
  reason?: string;
  createdAt?: Timestamp;
  completedAt?: Timestamp;
  totalUnits?: number;
  creditedCount?: number;
  skippedCount?: number;
  failedCount?: number;
  results?: UnitResultRow[];
}

function formatTs(ts: Timestamp | undefined): string {
  if (!ts?.toDate) return 'n/a';
  return ts.toDate().toLocaleString('en-GB');
}

export default function GiftCreditsAllUnitsPage() {
  const navigate = useNavigate();
  const [creditsInput, setCreditsInput] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [runs, setRuns] = useState<BulkRunRow[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [detailRun, setDetailRun] = useState<BulkRunRow | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, 'bulkCreditGiftRuns'),
      orderBy('createdAt', 'desc'),
      limit(30),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRuns(
          snap.docs.map((d) => {
            const data = d.data() as Omit<BulkRunRow, 'id'>;
            return { id: d.id, ...data };
          }),
        );
        setRunsLoading(false);
      },
      (err) => {
        console.error(err);
        setRunsLoading(false);
      },
    );
    return () => unsub();
  }, []);

  const sortedRuns = useMemo(() => runs, [runs]);

  const handleSubmit = async () => {
    const creditsPerUnit = parseInt(creditsInput, 10);
    if (!Number.isFinite(creditsPerUnit) || creditsPerUnit < 1) {
      showError('Enter a positive whole number of credits to gift per unit.');
      return;
    }
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      showError('Enter a reason for the audit trail.');
      return;
    }

    const idempotencyKey =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    setSubmitting(true);
    try {
      const fn = httpsCallable(functions, 'adminGiftCreditsToAllUnits');
      const res = await fn({
        creditsPerUnit,
        reason: trimmedReason,
        idempotencyKey,
      });
      const data = res.data as {
        runId?: string;
        alreadyCompleted?: boolean;
        creditedCount?: number;
        skippedCount?: number;
        failedCount?: number;
        totalUnits?: number;
      };
      if (data.alreadyCompleted) {
        showSuccess('This operation was already completed (idempotent replay).');
      } else {
        showSuccess(
          `Gift complete. Run ${data.runId}: credited ${data.creditedCount ?? 0} units, skipped ${data.skippedCount ?? 0}, failed ${data.failedCount ?? 0} (of ${data.totalUnits ?? 0}).`,
        );
      }
      setCreditsInput('');
      setReason('');
    } catch (e) {
      console.error(e);
      showError('Failed to gift credits. Check the console and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const failedRows = (detailRun?.results ?? []).filter((r) => r.status === 'failed');

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-5xl">
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-foreground">Gift credits to all units</h1>
            <p className="text-muted-foreground">
              Adds the same number of credits to every unit. Each unit receives a ledger entry under
              credit transactions, a matching platform ledger row, and a per-unit line in this run&apos;s
              log.
            </p>
          </div>

          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="h-5 w-5" />
                New bulk gift
              </CardTitle>
              <CardDescription>
                Irreversible once applied. Uses a fresh idempotency key each time so accidental double
                submission from this form is avoided.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="credits">Credits per unit</Label>
                <Input
                  id="credits"
                  type="number"
                  min={1}
                  step={1}
                  value={creditsInput}
                  onChange={(e) => setCreditsInput(e.target.value)}
                  placeholder="e.g. 10"
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reason">Reason (stored on each transaction)</Label>
                <Textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Platform goodwill credit, April 2026"
                  rows={3}
                  disabled={submitting}
                  maxLength={500}
                />
              </div>
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing…
                  </>
                ) : (
                  <>
                    <Gift className="mr-2 h-4 w-4" />
                    Gift all units
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent gift runs</CardTitle>
              <CardDescription>Audit log of bulk operations (newest first).</CardDescription>
            </CardHeader>
            <CardContent>
              {runsLoading ? (
                <p className="text-muted-foreground">Loading runs…</p>
              ) : sortedRuns.length === 0 ? (
                <p className="text-muted-foreground">No runs yet.</p>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Run ID</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Per unit</TableHead>
                        <TableHead className="text-right">Credited</TableHead>
                        <TableHead className="text-right">Skipped</TableHead>
                        <TableHead className="text-right">Failed</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedRuns.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell className="max-w-[140px] truncate font-mono text-xs" title={row.id}>
                            {row.id}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm">
                            {formatTs(row.createdAt)}
                          </TableCell>
                          <TableCell>{row.status ?? 'n/a'}</TableCell>
                          <TableCell className="text-right">{row.creditsPerUnit ?? 'n/a'}</TableCell>
                          <TableCell className="text-right">{row.creditedCount ?? 'n/a'}</TableCell>
                          <TableCell className="text-right">{row.skippedCount ?? 'n/a'}</TableCell>
                          <TableCell className="text-right">{row.failedCount ?? 'n/a'}</TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setDetailRun(row)}
                              disabled={!row.results?.length}
                            >
                              Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Dialog open={!!detailRun} onOpenChange={(open) => !open && setDetailRun(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Run {detailRun?.id}</DialogTitle>
          </DialogHeader>
          {detailRun?.reason ? (
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Reason:</span> {detailRun.reason}
            </p>
          ) : null}
          {failedRows.length > 0 ? (
            <div className="mt-4">
              <h4 className="mb-2 font-medium">Failed units</h4>
              <ul className="list-inside list-disc text-sm text-destructive">
                {failedRows.map((r) => (
                  <li key={r.unitId}>
                    {r.unitId}: {r.error ?? 'unknown error'}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="mt-4">
            <h4 className="mb-2 font-medium">All unit outcomes</h4>
            <div className="max-h-[50vh] overflow-auto rounded border text-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unit</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Balance after</TableHead>
                    <TableHead>Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(detailRun?.results ?? []).map((r) => (
                    <TableRow key={r.unitId}>
                      <TableCell className="font-mono text-xs">{r.unitId}</TableCell>
                      <TableCell>{r.status}</TableCell>
                      <TableCell className="text-right">
                        {typeof r.balanceAfter === 'number' ? r.balanceAfter : 'n/a'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {r.error ?? r.reason ?? 'n/a'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
